import _ from 'lodash';
import { HealthRecords, Patient, PatientDocument, Task } from 'generated/api';
import { useAppSelector, type RootState } from 'careand-redux/store';
import { useEffect } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { v4 as uuidV4 } from 'uuid';
import PatientUtils from 'services/PatientUtils';
import { using } from 'services/TypeUtils';

type PatientContext = {
  currentPatientId?: string;
  patient?: Patient;
  patientHR?: HealthRecords;
};

type TaskContext = {
  task?: Task;
};

type DocumentContext = {
  document?: PatientDocument;
};

export const AIContext = {
  patient: [] as (PatientContext & { stack_id: string })[],
  task: [] as (TaskContext & { stack_id: string })[],
  document: [] as (DocumentContext & { stack_id: string })[],
};

const PatientContextSelector = createSelector(
  (state: RootState) => state.patients.byId,
  (state: RootState) => state.healthRecords.byId,
  (_: unknown, patientId: string | undefined) => patientId,
  (patients, healthRecords, patientId): PatientContext | undefined => {
    if (!patientId) {
      return;
    }
    return {
      currentPatientId: patientId,
      patient: patients[patientId],
      patientHR: healthRecords[patientId]?.healthRecords,
    };
  },
);

const TaskContextSelector = createSelector(
  (state: RootState) => state.tasks.byId,
  (_: unknown, taskId?: string) => taskId,
  (tasks, taskId) => {
    if (!taskId) {
      return undefined;
    }
    return _(tasks)
      .values()
      .flatMap((it) => it ?? [])
      .find((it) => it.id === taskId);
  },
);

const DocumentContextSelector = createSelector(
  (state: RootState) => state.documents.all,
  (_: unknown, documentId?: string) => documentId,
  (documents, documentId) => {
    if (!documentId) {
      return undefined;
    }
    return _(documents).find((it) => it.id === documentId);
  },
);

export const useSpotlightPatientContext = (patientId?: string) => {
  const patientContext = useAppSelector((state) => PatientContextSelector(state, patientId));

  useEffect(() => {
    const uuid = uuidV4();
    if (patientContext) {
      const newContext = { ...patientContext, stack_id: uuid };

      const idx = AIContext.patient.findIndex((it) => it.stack_id === uuid);
      if (idx > -1) {
        AIContext.patient[idx] = newContext;
      } else {
        AIContext.patient.push(newContext);
      }
    }
    return () => {
      AIContext.patient = AIContext.patient.filter((it) => it.stack_id !== uuid);
    };
  }, [patientContext]);
  return patientContext;
};

export const useSpotlightTaskContext = (taskId?: string, task?: Task) => {
  const taskFromId = useAppSelector((state) => TaskContextSelector(state, taskId));

  useEffect(() => {
    const uuid = uuidV4();
    const effectiveTask = task ?? taskFromId;
    if (!effectiveTask) {
      return;
    }

    const newContext = { task: effectiveTask, stack_id: uuid };

    const idx = AIContext.task.findIndex((it) => it.stack_id === uuid);
    if (idx > -1) {
      AIContext.task[idx] = newContext;
    } else {
      AIContext.task.push(newContext);
    }

    console.log('[tc] updated', AIContext);
    return () => {
      AIContext.task = AIContext.task.filter((it) => it.stack_id !== uuid);
    };
  }, [task, taskFromId]);
};

export const useSpotlightDocumentContext = (documentId?: string, document?: Document) => {
  const documentFromId = useAppSelector((state) => DocumentContextSelector(state, documentId));

  useEffect(() => {
    const uuid = uuidV4();
    const effectiveDocument = document ?? documentFromId;
    if (!effectiveDocument) {
      return;
    }

    const newContext = { document: effectiveDocument, stack_id: uuid };

    const idx = AIContext.document.findIndex((it) => it.stack_id === uuid);
    if (idx > -1) {
      //@ts-expect-error
      AIContext.document[idx] = newContext;
    } else {
      //@ts-expect-error
      AIContext.document.push(newContext);
    }

    // console.log('[dc] updated', AIContext);
    return () => {
      AIContext.document = AIContext.document.filter((it) => it.stack_id !== uuid);
    };
  }, [document, documentFromId]);
};

export function getAIContext() {
  const res = {};

  // eslint-disable-next-line guard-for-in
  for (let key in AIContext) {
    Object.assign(res, _.last(AIContext[key as keyof typeof AIContext]));
  }

  return res;
}
